import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import LanguageSelector from '../../../components/Common/LanguageSelector';
import { useLanguage } from '../../../hooks/useLanguage';
import { header as frHeader } from '../../../locales/fr/header';
import { header as arHeader } from '../../../locales/ar/header';
import { header as esHeader } from '../../../locales/es/header';
import { aside as frAside } from '../../../locales/fr/aside';
import { aside as arAside } from '../../../locales/ar/aside';
import { aside as esAside } from '../../../locales/es/aside';
import Aside from './Aside';
import useMyAppStore from '../../../stores/myAppStore';
import { useResponsiveView } from '../../../hooks/useResponsiveView';
import { useTheme } from '../../../hooks/useTheme';

const translations = {
  fr: frHeader,
  ar: arHeader,
  es: esHeader
};

const asideTranslations = {
  fr: frAside,
  ar: arAside,
  es: esAside
};

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = translations[lang];
  const asideT = asideTranslations[lang];
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { pageTitle, resetAll } = useMyAppStore();
  const { isDesktop } = useResponsiveView();
  const theme = useTheme();

  const handleLogout = () => {
    logout();
    resetAll();
    console.log('🧹 Store vidé après déconnexion');
    navigate(`/${lang}/login`);
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleThemeToggle = () => {
    useMyAppStore.getState().toggleTheme();
  };

  const handleQuickAccess = () => {
    navigate(`/${lang}/myapp/quick-access`);
  };

  return (
    <>
      <Aside isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      
      <div className="sticky top-0 z-[90] print-hide" dir="ltr">
        <header className="bg-white dark:bg-gray-800 shadow-lg transition-colors duration-200">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center min-w-0 flex-1">
                <button
                  onClick={handleMenuToggle}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 mr-4 flex-shrink-0 transition-colors duration-200"
                >
                  <span className="sr-only">Menu</span>
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                <div className="flex items-center space-x-4 min-w-0 flex-1">
                  <Link 
                    to={`/${lang}/myapp/quick-access`} 
                    className="flex items-center space-x-2 flex-shrink-0"
                  >
                    <span className="text-3xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent">
                      OSC
                    </span>
                  </Link>
                  <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 flex-shrink-0"></div>
                  <div className="min-w-0 flex-1">
                    <h1 
                      className="text-xl font-semibold text-gray-800 dark:text-gray-100 truncate transition-colors duration-200"
                      title={pageTitle}
                    >
                      {pageTitle}
                    </h1>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4 flex-shrink-0">
                <button
                  onClick={handleQuickAccess}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 transition-colors duration-200"
                  title={asideT.routes.quickAccess || 'Accès Rapide'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>

                {isDesktop && (
                  <span className="text-gray-700 dark:text-gray-300 transition-colors duration-200">
                    {user.firstName} {user.lastName}
                  </span>
                )}
                <button
                  onClick={handleThemeToggle}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 transition-colors duration-200"
                  title={theme === 'light' ? 'Passer en mode sombre' : 'Passer en mode clair'}
                >
                  {theme === 'light' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  )}
                </button>
                <LanguageSelector 
                  textColor={theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
                  hoverColor={theme === 'light' ? 'hover:text-indigo-600' : 'hover:text-indigo-400'}
                  bgColor={theme === 'light' ? 'bg-white' : 'bg-gray-800'}
                  iconColor={theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
                  dropdownBgColor={theme === 'light' ? 'bg-white' : 'bg-gray-800'}
                  dropdownTextColor={theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
                  dropdownHoverBgColor={theme === 'light' ? 'hover:bg-gray-50' : 'hover:bg-gray-700'}
                  dropdownSelectedBgColor={theme === 'light' ? 'bg-indigo-50' : 'bg-indigo-900'}
                  dropdownSelectedTextColor={theme === 'light' ? 'text-indigo-600' : 'text-indigo-300'}
                />
                <button
                  onClick={handleLogout}
                  className={`
                    p-2 rounded-md flex items-center justify-center
                    transition-colors duration-200
                    ${theme === 'light' 
                      ? 'bg-indigo-600 hover:bg-indigo-700' 
                      : 'bg-indigo-600 hover:bg-indigo-600'
                    }
                    text-white
                  `}
                  title={t.auth.logout}
                >
                  {isDesktop ? (
                    <span className="text-sm font-medium">{t.auth.logout}</span>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </nav>
        </header>
      </div>
    </>
  );
};

export default Header; 